import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import type { z } from 'zod'
import { Button } from '../../components/ui/Button'
import { Field } from '../../components/ui/Field'
import { FormCard } from '../../components/ui/FormCard'
import { PageTitle } from '../../components/ui/PageTitle'
import { offerSchema } from '../../schemas/adminSchemas'
import { api } from '../../lib/api'

type OfferForm = z.infer<typeof offerSchema>

const now = new Date()
const toLocal = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)

export function OfferFormPage({ mode }: { mode: 'new' | 'edit' }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const disabled = false

  const form = useForm<OfferForm>({
    resolver: zodResolver(offerSchema),
    defaultValues: {
      code: '', discountType: 'flat', value: 0, maxDiscount: 0, minOrder: 0,
      usageLimit: 1000, perUserLimit: 1, validFrom: toLocal(now),
      validTo: toLocal(new Date(now.getTime() + 30 * 864e5)),
      description: '', status: 'Active',
    },
  })

  useEffect(() => {
    if (mode === 'edit' && id) {
      api.get(`/admin/offers/${id}`).then(({ data }) => {
        const o = data.offer
        form.reset({
          code: o.code, discountType: o.discountType, value: o.value,
          maxDiscount: o.maxDiscount ?? 0, minOrder: o.minOrder,
          usageLimit: o.usageLimit, perUserLimit: o.perUserLimit,
          validFrom: toLocal(new Date(o.validFrom)),
          validTo: toLocal(new Date(o.validTo)),
          description: o.description ?? '', status: o.status,
        })
      })
    }
  }, [mode, id, form])

  const onSubmit = async (values: OfferForm) => {
    if (mode === 'new') await api.post('/admin/offers', values)
    else await api.put(`/admin/offers/${id}`, values)
    navigate('/offers')
  }

  const title = mode === 'new' ? 'Add Offer' : 'Edit Offer'

  return (
    <div className="space-y-6">
      <PageTitle title={title} subtitle="Create and maintain coupon rules."
        action={<NavLink to="/offers"><Button icon={ArrowLeft} variant="soft">Back to Offers</Button></NavLink>} />
      <FormCard title="Offer Form" submitLabel={form.formState.isSubmitting ? 'Saving…' : 'Save Offer'} onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Coupon Code" disabled={disabled} error={form.formState.errors.code?.message} {...form.register('code')} />
          <Field as="select" label="Discount Type" {...form.register('discountType')}>
            <option value="flat">Flat (₹)</option>
            <option value="percent">Percent (%)</option>
          </Field>
          <Field label="Discount Value" type="number" error={form.formState.errors.value?.message} {...form.register('value', { valueAsNumber: true })} />
          <Field label="Max Discount (₹)" type="number" {...form.register('maxDiscount', { valueAsNumber: true })} />
          <Field label="Min Order Amount" type="number" {...form.register('minOrder', { valueAsNumber: true })} />
          <Field label="Usage Limit" type="number" {...form.register('usageLimit', { valueAsNumber: true })} />
          <Field label="Per User Limit" type="number" {...form.register('perUserLimit', { valueAsNumber: true })} />
          <Field label="Valid From" type="datetime-local" error={form.formState.errors.validFrom?.message} {...form.register('validFrom')} />
          <Field label="Valid To" type="datetime-local" error={form.formState.errors.validTo?.message} {...form.register('validTo')} />
          <Field as="select" label="Status" {...form.register('status')}>
            <option>Active</option><option>Inactive</option>
          </Field>
          <div className="sm:col-span-2">
            <Field as="textarea" label="Description" {...form.register('description')} />
          </div>
        </div>
      </FormCard>
    </div>
  )
}
